import { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/api';
import './ChatPanel.css';

const ROLE_COLORS = { admin: '#f59e0b', cleaner: '#65a30d', client: '#0284c7' };

function Avatar({ name, role, size = 40 }) {
  const initial = (name || '?')[0].toUpperCase();
  return (
    <div
      className="cp-avatar"
      style={{ width: size, height: size, fontSize: size * 0.42, background: ROLE_COLORS[role] || '#0284c7' }}
    >
      {initial}
    </div>
  );
}

/* ───── User Picker Modal ───── */
function UserPickerModal({ visible, onClose, onSelect }) {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!visible) return;
    setLoading(true);
    api.get('/chat/users').then(res => {
      setUsers(res.data?.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [visible]);

  if (!visible) return null;

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  });

  const grouped = {};
  filtered.forEach(u => {
    const r = u.role || 'other';
    if (!grouped[r]) grouped[r] = [];
    grouped[r].push(u);
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content cp-picker-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t('dashboard.newChat', 'New Chat')}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ padding: 0 }}>
          <div className="cp-search-wrap">
            <input
              type="text"
              className="cp-search-input"
              placeholder={t('dashboard.searchUsers', 'Search by name or email...')}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          {loading ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#999' }}>{t('common.loading', 'Loading...')}</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#999' }}>{t('dashboard.noUsersFound', 'No users found')}</div>
          ) : (
            <div className="cp-picker-list">
              {Object.entries(grouped).map(([role, list]) => (
                <div key={role}>
                  <div className="cp-group-title">{role}s</div>
                  {list.map(u => (
                    <div key={u.id} className="cp-picker-item" onClick={() => { onSelect(u); onClose(); }}>
                      <Avatar name={u.name} role={u.role} size={36} />
                      <div className="cp-picker-info">
                        <span className="cp-picker-name">{u.name}</span>
                        <span className="cp-picker-email">{u.email}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ───── Chat Thread ───── */
function ChatThread({ partner, onBack }) {
  const { t } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const currentUserId = parseInt(localStorage.getItem('userId'));

  const fetchMessages = useCallback(async () => {
    try {
      const res = await api.get(`/chat/messages/${partner.id}?limit=100`);
      setMessages(res.data?.data || []);
    } catch { /* silent */ }
  }, [partner.id]);

  useEffect(() => {
    fetchMessages();
    // Mark as read
    api.put(`/chat/read/${partner.id}`).catch(() => {});
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [partner.id, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);

    // Optimistic
    const tempMsg = {
      id: `temp-${Date.now()}`,
      senderId: currentUserId,
      receiverId: partner.id,
      text: trimmed,
      createdAt: new Date().toISOString(),
      isRead: false,
      _sending: true
    };
    setMessages(prev => [...prev, tempMsg]);
    setText('');

    try {
      await api.post('/chat/send', { receiverId: partner.id, text: trimmed });
      fetchMessages();
    } catch { /* silent */ }
    setSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (d) => {
    if (!d) return '';
    const date = new Date(d);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    if (isToday) return time;
    return `${date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} ${time}`;
  };

  return (
    <div className="cp-thread">
      <div className="cp-thread-header">
        <button className="cp-back-btn" onClick={onBack}>&#8592;</button>
        <Avatar name={partner.name} role={partner.role} size={36} />
        <div className="cp-thread-partner">
          <span className="cp-thread-name">{partner.name}</span>
          <span className="cp-role-tag">{partner.role}</span>
        </div>
      </div>
      <div className="cp-messages">
        {messages.map(msg => {
          const isMe = msg.senderId === currentUserId;
          return (
            <div key={msg.id} className={`cp-bubble ${isMe ? 'me' : 'other'}`}>
              <p className="cp-bubble-text">{msg.text}</p>
              <span className="cp-bubble-time">
                {msg._sending ? t('dashboard.sending', 'Sending...') : formatTime(msg.createdAt)}
                {isMe && !msg._sending && msg.isRead && ' \u2713\u2713'}
              </span>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>
      <div className="cp-input-row">
        <textarea
          className="cp-chat-input"
          rows={1}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('dashboard.typeMessage', 'Type a message...')}
        />
        <button className="cp-send-btn" onClick={handleSend} disabled={!text.trim() || sending}>
          &#10148;
        </button>
      </div>
    </div>
  );
}

/* ───── Main ChatPanel ───── */
export default function ChatPanel({ onUnreadCountChange }) {
  const { t } = useLanguage();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const userRole = localStorage.getItem('userRole');

  const fetchConversations = useCallback(async (showSpinner) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await api.get('/chat/conversations');
      setConversations(res.data?.data || []);
    } catch { /* silent */ }
    if (showSpinner) setLoading(false);
  }, []);

  useEffect(() => {
    fetchConversations(true);
    const interval = setInterval(() => fetchConversations(false), 10000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  useEffect(() => {
    const total = conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
    if (onUnreadCountChange) onUnreadCountChange(total);
  }, [conversations, onUnreadCountChange]);

  const openChat = (partner) => {
    setSelectedPartner(partner);
  };

  const handleNewChatSelect = (user) => {
    setSelectedPartner({ id: user.id, name: user.name, role: user.role });
  };

  const formatPreviewTime = (d) => {
    if (!d) return '';
    const date = new Date(d);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    const diffD = Math.floor((now - date) / 86400000);
    if (diffD < 7) return date.toLocaleDateString('en-GB', { weekday: 'short' });
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  };

  if (selectedPartner) {
    return (
      <div className="cp-container">
        <ChatThread
          partner={selectedPartner}
          onBack={() => { setSelectedPartner(null); fetchConversations(false); }}
        />
      </div>
    );
  }

  return (
    <div className="cp-container">
      <div className="cp-conv-header">
        <div>
          <h2>{t('dashboard.chat', 'Chat')}</h2>
          <p className="cp-subtitle">
            {userRole === 'admin'
              ? t('dashboard.chatWithAll', 'Chat with clients & cleaners')
              : t('dashboard.chatWithSupport', 'Chat with support')}
          </p>
        </div>
        <button className="cp-new-chat-btn" onClick={() => setShowPicker(true)} title={t('dashboard.newChat', 'New Chat')}>
          +
        </button>
      </div>

      {loading ? (
        <div className="np-loading">{t('common.loading', 'Loading...')}</div>
      ) : conversations.length === 0 ? (
        <div className="np-empty">
          <div className="np-empty-icon">💬</div>
          <p>{t('dashboard.noConversations', 'No conversations yet')}</p>
          <span>{t('dashboard.sayHello', 'Say hello!')}</span>
        </div>
      ) : (
        <div className="cp-conv-list">
          {conversations.map(conv => {
            const partner = conv.partner || {};
            return (
              <div
                key={partner.id}
                className="cp-conv-item"
                onClick={() => openChat(partner)}
              >
                <Avatar name={partner.name} role={partner.role} />
                <div className="cp-conv-body">
                  <div className="cp-conv-top">
                    <span className="cp-conv-name">{partner.name}</span>
                    <span className="cp-role-tag">{partner.role}</span>
                  </div>
                  <p className="cp-conv-preview">
                    {conv.lastMessage?.senderId === parseInt(localStorage.getItem('userId')) ? `${t('dashboard.you', 'You')}: ` : ''}
                    {conv.lastMessage?.text || ''}
                  </p>
                </div>
                <div className="cp-conv-right">
                  <span className="cp-conv-time">{formatPreviewTime(conv.lastMessage?.createdAt)}</span>
                  {conv.unreadCount > 0 && (
                    <span className="cp-conv-badge">{conv.unreadCount > 99 ? '99+' : conv.unreadCount}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <UserPickerModal
        visible={showPicker}
        onClose={() => setShowPicker(false)}
        onSelect={handleNewChatSelect}
      />
    </div>
  );
}
